"use client";

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { useMsal } from "@azure/msal-react";
import { useAuthFetch } from "../hooks/useAuthFetch";
import {
  forceUnlockAllSheetModals,
  useFullscreenSheetModal,
} from "../hooks/useFullscreenSheetModal";
import { DailyLogBackButton, DailyLogBreadcrumbs, DailyLogProjectFields } from "./DailyLogFlowNav";

/* ================= ICON ================= */

const CloseIcon = ({ onClick }) => (
  <button
    type="button"
    onClick={onClick}
    aria-label="Close"
    className="flex items-center justify-center w-10 h-10 sm:w-9 sm:h-9 rounded-md hover:bg-gray-100 touch-manipulation"
  >
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="18"
      height="18"
      viewBox="0 0 18 18"
      fill="none"
      className="shrink-0"
    >
      <path
        d="M17.7072 0.292849C17.3167 -0.0976164 16.6836 -0.0976164 16.2932 0.292849L8.99999 7.58604L1.70683 0.292849C1.31637 -0.0976164 0.683306 -0.0976164 0.29285 0.292849C-0.0976166 0.683305 -0.0976166 1.31637 0.29285 1.70683L7.58602 9.00001L0.29287 16.2931C-0.0975966 16.6836 -0.0975966 17.3166 0.29287 17.7072C0.683326 18.0976 1.31639 18.0976 1.70685 17.7072L8.99999 10.414L16.2932 17.7072C16.6836 18.0976 17.3167 18.0976 17.7072 17.7072C18.0976 17.3166 18.0976 16.6836 17.7072 16.2932L10.414 9.00001L17.7072 1.70683C18.0976 1.31637 18.0976 0.683305 17.7072 0.292849Z"
        fill="#424143"
      />
    </svg>
  </button>
);

const getInventoryValidationBannerContent = (block) => ({
  title: block.title || "Cannot save",
  subtitle: block.subtitle || null,
  chips: block.chips ?? [],
});

const SaveValidationBanners = ({ validationBlocks, onDismiss }) => {
  if (!validationBlocks?.length) return null;

  return (
    <div className="flex shrink-0 flex-col gap-2 border-b border-gray-200 bg-white px-6 py-3">
      {validationBlocks.map((block, idx) => {
        const { title, subtitle, chips } = getInventoryValidationBannerContent(block);
        return (
          <div
            key={`${block.messageVariant}-${idx}`}
            role="alert"
            className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="currentColor"
              className="mt-0.5 h-5 w-5 shrink-0 text-amber-500"
              aria-hidden="true"
            >
              <path
                fillRule="evenodd"
                d="M9.401 3.003c1.155-2 4.043-2 5.197 0l7.355 12.748c1.154 2-.29 4.5-2.599 4.5H4.645c-2.309 0-3.752-2.5-2.598-4.5L9.4 3.003ZM12 8.25a.75.75 0 0 1 .75.75v3.75a.75.75 0 0 1-1.5 0V9a.75.75 0 0 1 .75-.75Zm0 8.25a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5Z"
                clipRule="evenodd"
              />
            </svg>
            <div className="min-w-0 flex-1">
              <p className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-sm font-semibold text-amber-900">
                <span>{title}</span>
                {chips.map((chip) => (
                  <span
                    key={typeof chip === "string" ? chip : chip.key}
                    className="inline-flex cursor-default items-center rounded-full border border-amber-200 bg-white px-2.5 py-0.5 text-xs font-medium text-amber-900"
                  >
                    {typeof chip === "string" ? chip : chip.label}
                  </span>
                ))}
              </p>
              {subtitle && (
                <p className="mt-0.5 text-sm text-amber-800/90">{subtitle}</p>
              )}
            </div>
            <button
              type="button"
              onClick={() => onDismiss(idx)}
              className="shrink-0 text-sm text-amber-700 underline hover:text-amber-900"
            >
              Dismiss
            </button>
          </div>
        );
      })}
    </div>
  );
};

const Header = ({ onClose, onFlowStep, navDisabled }) => (
  <div className="relative z-10 flex shrink-0 items-center justify-between gap-3 sm:gap-4 bg-white py-3.5 sm:py-[18px] px-4 sm:px-6 rounded-t-xl border-b border-gray-200 shadow-[0_2px_5px_rgba(0,0,0,0.10)]">
    {onFlowStep ? (
      <DailyLogBreadcrumbs
        currentStep="inventory"
        onStepChange={onFlowStep}
        disabled={navDisabled}
      />
    ) : (
      <h2 className="font-poppins text-[18px] sm:text-[20px] font-bold text-[#2C2C2C]">
        Inventory Tracking
      </h2>
    )}
    <CloseIcon onClick={onClose} />
  </div>
);

const formatIsoDateToMdy = (isoDate) => {
  if (!isoDate || typeof isoDate !== "string") return "--";
  const parts = isoDate.split("-");
  if (parts.length !== 3) return isoDate;
  const [yyyy, mm, dd] = parts;
  return `${mm}-${dd}-${yyyy}`;
};

const getSavedByPayload = (accounts) => {
  const account = accounts?.[0];
  const savedByName =
    (typeof window !== "undefined" && window.__currentUserDisplayName) ||
    account?.name ||
    "";
  const savedByEmail = account?.username || "";
  return {
    savedByName: String(savedByName).trim(),
    savedByEmail: String(savedByEmail).trim(),
  };
};

const parseNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const PARTIAL_NUMBER_INPUT_RE = /^\d*\.?\d*$/;

const isValidPartialNumberInput = (value) => value === '' || PARTIAL_NUMBER_INPUT_RE.test(value);

const normalizeNumberOnBlur = (value) => {
  if (value === '' || value == null) return 0;
  const parsed = parseFloat(value);
  return Number.isFinite(parsed) ? Math.max(0, parsed) : 0;
};

const getQtyDisplayValue = (value) => {
  if (value === '') return '';
  if (value == null) return '0';
  return String(value);
};

const formatNumberValue = (value) => {
  if (value == null || value === "" || value === "--") return "--";
  const parsed = parseFloat(value);
  if (Number.isNaN(parsed)) return String(value);
  return Math.round(parsed).toLocaleString();
};

/** Live Taken To Date / % Onsite (net = Total Taken minus Returned from Project). */
const applyTakenToProjectCalculations = (item) => {
  const totalEstimated =
    item.totalEstimatedNum != null
      ? parseNumber(item.totalEstimatedNum)
      : parseNumber(item.totalEstimated);
  const totalTaken = item.takenToProject === '' ? 0 : parseNumber(item.takenToProject);
  const returnedFromProject =
    item.returnedFromProject === '' ? 0 : parseNumber(item.returnedFromProject);
  const netOnsite = Math.max(0, totalTaken - returnedFromProject);
  const percentOnsite =
    totalEstimated > 0 ? `${Math.round((netOnsite / totalEstimated) * 100)}%` : "--";

  return {
    ...item,
    takenToDate: formatNumberValue(netOnsite),
    totalEstimated: formatNumberValue(totalEstimated),
    totalEstimatedNum: totalEstimated,
    percentOnsite,
  };
};

const mapStockRowToAddOption = (row) => {
  const id = String(row.id);
  return {
    id,
    label: row.description,
    stockItemId: row.id,
  };
};

const tableGridColumns =
  "grid-cols-[118px_248px_80px_132px_156px_132px_132px_118px_minmax(220px,1fr)]";

const getDateRowBgClass = (dateRowIndex) =>
  dateRowIndex % 2 === 0 ? "bg-white" : "bg-[#F3F7FF]";

const MULTI_ITEM_ROW_HEIGHT = 44;
const MULTI_ITEM_NOTES_HEIGHT = 32;

const InventoryTableRow = ({
  date,
  items,
  isEditable,
  isEmptyForDate,
  dateRowIndex = 0,
  onChangeField,
  onBlurField,
  onChangeNotes,
}) => {
  const rowBgClass = getDateRowBgClass(dateRowIndex);

  if (isEmptyForDate || (isEditable && items.length === 0)) {
    return (
      <div className={`grid ${tableGridColumns} border-b ${rowBgClass}`}>
        <div className="flex items-center justify-center text-sm border-r h-[56px]">{date}</div>
        <div className="col-span-8 flex items-center px-4 text-sm text-gray-500 italic h-[56px]">
          No data saved for {date}.
        </div>
      </div>
    );
  }

  const singleItem = items.length === 1;
  const rowItemHeight = singleItem ? 72 : MULTI_ITEM_ROW_HEIGHT;
  const notesHeight = singleItem ? 56 : MULTI_ITEM_NOTES_HEIGHT;
  const dateCellMinHeight = singleItem ? rowItemHeight : items.length * MULTI_ITEM_ROW_HEIGHT;
  const isQtyEditable = (colKey) =>
    isEditable && (colKey === "takenToProject" || colKey === "returnedFromProject");

  const inputCellClass = "flex items-center justify-center px-2.5 w-full box-border";
  const qtyInputClass =
    "w-full max-w-[108px] h-[32px] border border-gray-300 rounded text-center text-sm font-semibold focus:outline-none focus:ring-1 focus:ring-blue-500";
  const readonlyInputClass =
    "w-full max-w-[108px] h-[32px] border border-gray-300 rounded text-center text-sm font-semibold focus:outline-none";

  return (
    <div className={`grid ${tableGridColumns} border-b`}>
      <div
        className={`flex items-center justify-center text-sm border-r ${rowBgClass}`}
        style={{ minHeight: dateCellMinHeight }}
      >
        {date}
      </div>

      <div className="border-r">
        {items.map((item, idx) => (
          <div
            key={item.itemKey || idx}
            className={`flex items-center justify-center px-3 text-sm text-center leading-tight ${rowBgClass}`}
            style={{ height: rowItemHeight }}
          >
            {item.description}
          </div>
        ))}
      </div>

      <div className="border-r">
        {items.map((item, idx) => (
          <div
            key={item.itemKey || idx}
            className={`flex items-center justify-center text-sm ${rowBgClass}`}
            style={{ height: rowItemHeight }}
          >
            {item.uom}
          </div>
        ))}
      </div>

      {["takenToProject", "returnedFromProject"].map((colKey) => (
        <div key={colKey} className="border-r">
          {items.map((item, idx) => (
            <div
              key={item.itemKey || idx}
              className={`${inputCellClass} ${rowBgClass}`}
              style={{ height: rowItemHeight }}
            >
              <input
                type="text"
                inputMode="decimal"
                value={getQtyDisplayValue(item[colKey])}
                onChange={
                  isQtyEditable(colKey)
                    ? (e) => onChangeField?.(item.itemKey, colKey, e.target.value)
                    : undefined
                }
                onFocus={isQtyEditable(colKey) ? (e) => e.target.select() : undefined}
                onBlur={
                  isQtyEditable(colKey)
                    ? () => onBlurField?.(item.itemKey, colKey)
                    : undefined
                }
                readOnly={!isQtyEditable(colKey)}
                className={qtyInputClass}
                style={{
                  backgroundColor: isQtyEditable(colKey) ? "#FFFFFF" : "#F3F4F6",
                  color: "#111827",
                }}
              />
            </div>
          ))}
        </div>
      ))}

      {["takenToDate", "totalEstimated", "percentOnsite"].map((colKey) => (
        <div key={colKey} className="border-r">
          {items.map((item, idx) => (
            <div
              key={item.itemKey || idx}
              className={`${inputCellClass} ${rowBgClass}`}
              style={{ height: rowItemHeight }}
            >
              <input
                type="text"
                value={item[colKey] ?? ""}
                readOnly
                className={readonlyInputClass}
                style={{ backgroundColor: "#F3F4F6", color: "#111827" }}
              />
            </div>
          ))}
        </div>
      ))}

      <div className="border-r last:border-r-0 min-w-0">
        {items.map((item, idx) => (
          <div
            key={item.itemKey || idx}
            className={`w-full min-w-0 px-2.5 flex items-center box-border ${rowBgClass}`}
            style={{ height: rowItemHeight }}
          >
            <textarea
              placeholder="Daily Notes"
              value={item.dailyNotes ?? ""}
              onChange={isEditable ? (e) => onChangeNotes?.(item.itemKey, e.target.value) : undefined}
              readOnly={!isEditable}
              className="w-full min-w-0 border border-gray-300 rounded px-3 py-1 resize-none text-sm leading-[1.25] focus:outline-none focus:ring-1 focus:ring-blue-500"
              style={{
                height: notesHeight,
                backgroundColor: isEditable ? "#FFFFFF" : "#F3F4F6",
                color: "#111827",
              }}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

const buildInventoryTableRows = (tableRows, handlers) =>
  tableRows.map((row, dateRowIndex) => (
    <InventoryTableRow
      key={row.workDateIso}
      date={row.date}
      items={row.items}
      isEditable={row.isEditable}
      isEmptyForDate={row.isEmptyForDate}
      dateRowIndex={dateRowIndex}
      onChangeField={handlers.onChangeField}
      onBlurField={handlers.onBlurField}
      onChangeNotes={handlers.onChangeNotes}
    />
  ));

const InventoryTable = ({
  tableRows,
  loading,
  onChangeField,
  onBlurField,
  onChangeNotes,
  compactTopSpacing = false,
}) => {
  const topSpacingClass = compactTopSpacing ? "mt-2" : "mt-6";
  return (
  <section className={`w-full ${topSpacingClass} px-4 md:px-6 overflow-x-hidden`}>
    <div className="border border-gray-300 rounded-md bg-white overflow-x-auto max-w-full">
      <div className="w-full min-w-[1336px]">
        <div
          className={`grid ${tableGridColumns} bg-gray-200 border-b border-gray-300 font-semibold text-sm`}
        >
          {[
            "Date",
            "Description",
            "UOM",
            "Taken",
            "Returned",
            "Taken To Date",
            "Total Estimated",
            "% Onsite",
            "Daily Notes",
          ].map((h, i) => (
            <div
              key={h}
              className={`flex items-center justify-center text-center h-[56px] border-r px-2 leading-tight ${
                i === 8 ? "last:border-r-0 min-w-0" : ""
              }`}
            >
              {h}
            </div>
          ))}
        </div>
        {loading ? (
          <div className="px-4 py-8 text-sm text-gray-500 text-center">Loading inventory data…</div>
        ) : tableRows.length === 0 ? (
          <div className="px-4 py-8 text-sm text-gray-500 text-center">
            No inventory entries yet. Use Add Inventory Item to add rows for the selected work date.
          </div>
        ) : (
          buildInventoryTableRows(tableRows, { onChangeField, onBlurField, onChangeNotes })
        )}
      </div>
    </div>
  </section>
  );
};

const InventoryTrackingContent = ({ onClose, initialContext, onFlowNavigate, isActive = true }) => {
  const { accounts } = useMsal();
  const { getAuthConfig, isReady } = useAuthFetch();
  const [projectNo, setProjectNo] = useState(initialContext?.projectNo ?? "");
  const [projectName, setProjectName] = useState(initialContext?.projectName ?? "");
  const [workDate, setWorkDate] = useState(initialContext?.workDate ?? "");
  const [division, setDivision] = useState(initialContext?.division ?? "");
  const [tableRows, setTableRows] = useState([]);
  const [tableLoading, setTableLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [addingItems, setAddingItems] = useState(false);
  const [saveMessage, setSaveMessage] = useState(null);
  const closeAfterSaveTimeoutRef = useRef(null);

  const [isAddInventoryOpen, setIsAddInventoryOpen] = useState(false);
  const [selectedInventoryItemKeys, setSelectedInventoryItemKeys] = useState([]);
  const [stockInventoryAddOptions, setStockInventoryAddOptions] = useState([]);
  const [stockInventoryLoading, setStockInventoryLoading] = useState(false);
  const [stockInventoryError, setStockInventoryError] = useState(null);

  useEffect(() => {
    return () => {
      if (closeAfterSaveTimeoutRef.current) {
        window.clearTimeout(closeAfterSaveTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (isActive) return undefined;
    if (closeAfterSaveTimeoutRef.current) {
      window.clearTimeout(closeAfterSaveTimeoutRef.current);
      closeAfterSaveTimeoutRef.current = null;
    }
    return undefined;
  }, [isActive]);

  useEffect(() => {
    if (!initialContext) return;
    if (initialContext.projectNo != null) setProjectNo(initialContext.projectNo);
    if (initialContext.projectName != null) setProjectName(initialContext.projectName);
    if (initialContext.workDate != null) setWorkDate(initialContext.workDate);
    if (initialContext.division != null) setDivision(initialContext.division);
  }, [initialContext]);

  const fetchInventoryTable = useCallback(async ({ silent = false } = {}) => {
    const baseUrl = process.env.REACT_APP_BACKEND_BASEURL;
    const trimmedProject = (projectNo || "").trim();
    const trimmedDivision = (division || "").trim();
    const dateKey = (workDate || "").trim();

    if (!baseUrl || !trimmedProject || !trimmedDivision || !isReady) {
      setTableRows([]);
      return;
    }

    if (!silent) setTableLoading(true);
    try {
      const params = new URLSearchParams({
        division: trimmedDivision,
      });
      if (dateKey) params.set("workDate", dateKey);

      const res = await axios.get(
        `${baseUrl}/api/miter/production-performance/${encodeURIComponent(trimmedProject)}/inventory-entry?${params}`,
        await getAuthConfig()
      );
      if (res.data?.status === "success" && Array.isArray(res.data?.data?.tableRows)) {
        setTableRows(res.data.data.tableRows);
      } else {
        setTableRows([]);
      }
    } catch (err) {
      setTableRows([]);
      setSaveMessage({
        type: "error",
        validationBlocks: [{
          messageVariant: "generic",
          title: "Cannot load inventory data",
          subtitle: err.response?.data?.message || err.message || "Failed to load inventory tracking data.",
        }],
      });
    } finally {
      if (!silent) setTableLoading(false);
    }
  }, [projectNo, division, workDate, isReady, getAuthConfig]);

  useEffect(() => {
    fetchInventoryTable();
  }, [fetchInventoryTable]);

  useEffect(() => {
    if (!saveMessage) return undefined;
    const delayMs = saveMessage.type === "error" ? 15000 : 5000;
    const timeoutId = window.setTimeout(() => setSaveMessage(null), delayMs);
    return () => window.clearTimeout(timeoutId);
  }, [saveMessage]);

  useEffect(() => {
    if (!isAddInventoryOpen) return;
    setSelectedInventoryItemKeys([]);
  }, [isAddInventoryOpen]);

  useEffect(() => {
    if (!isAddInventoryOpen || !isReady) return;

    const trimmedDivision = (division || "").trim();
    if (!trimmedDivision) {
      setStockInventoryAddOptions([]);
      setStockInventoryError("Activity is required to load inventory items.");
      return;
    }

    const baseUrl = process.env.REACT_APP_BACKEND_BASEURL;
    if (!baseUrl) {
      setStockInventoryAddOptions([]);
      setStockInventoryError("Backend URL not configured.");
      return;
    }

    let cancelled = false;
    setStockInventoryLoading(true);
    setStockInventoryError(null);

    const qs = `?activityClass=${encodeURIComponent(trimmedDivision)}`;
    (async () => {
      try {
        const res = await axios.get(
          `${baseUrl}/api/miter/production-performance/stock-inventory-items${qs}`,
          await getAuthConfig()
        );
        if (cancelled) return;
        if (res.data?.status === "success" && Array.isArray(res.data?.data)) {
          setStockInventoryAddOptions(res.data.data.map(mapStockRowToAddOption));
        } else {
          setStockInventoryAddOptions([]);
        }
      } catch (err) {
        if (cancelled) return;
        setStockInventoryAddOptions([]);
        setStockInventoryError(
          err.response?.data?.message || err.message || "Failed to load inventory items."
        );
      } finally {
        if (!cancelled) setStockInventoryLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isAddInventoryOpen, division, isReady, getAuthConfig]);

  const currentItemKeys = useMemo(() => {
    const keys = new Set();
    tableRows.forEach((row) => {
      if (!row.isEditable) return;
      (row.items || []).forEach((item) => {
        if (item.itemKey) keys.add(String(item.itemKey));
      });
    });
    return keys;
  }, [tableRows]);

  const availableInventoryAddOptions = useMemo(
    () => stockInventoryAddOptions.filter((opt) => !currentItemKeys.has(opt.id)),
    [stockInventoryAddOptions, currentItemKeys]
  );

  const selectedInventoryRows = useMemo(
    () => stockInventoryAddOptions.filter((opt) => selectedInventoryItemKeys.includes(opt.id)),
    [stockInventoryAddOptions, selectedInventoryItemKeys]
  );

  const handleAddInventoryItemsUpdate = async () => {
    if (selectedInventoryRows.length === 0) {
      setIsAddInventoryOpen(false);
      return;
    }

    const baseUrl = process.env.REACT_APP_BACKEND_BASEURL;
    const trimmedProject = (projectNo || "").trim();
    const trimmedDivision = (division || "").trim();
    const dateKey = (workDate || "").trim();

    if (!baseUrl || !trimmedProject || !trimmedDivision || !dateKey) {
      setSaveMessage({
        type: "error",
        validationBlocks: [{
          messageVariant: "generic",
          title: "Cannot add inventory items",
          subtitle: "Project, division, and work date are required to add inventory items.",
        }],
      });
      return;
    }

    if (!/^\d{4}-\d{2}-\d{2}$/.test(dateKey)) {
      setSaveMessage({
        type: "error",
        validationBlocks: [{
          messageVariant: "generic",
          title: "Cannot add inventory items",
          subtitle: "Work date must be YYYY-MM-DD.",
        }],
      });
      return;
    }

    setAddingItems(true);
    setSaveMessage(null);
    try {
      const res = await axios.post(
        `${baseUrl}/api/miter/production-performance/${encodeURIComponent(trimmedProject)}/inventory-entry/add-items`,
        {
          workDate: dateKey,
          division: trimmedDivision,
          stockItemIds: selectedInventoryRows.map((r) => Number(r.stockItemId)),
        },
        await getAuthConfig()
      );
      if (res.data?.status === "success" && Array.isArray(res.data?.data?.tableRows)) {
        setTableRows(res.data.data.tableRows);
      } else {
        await fetchInventoryTable({ silent: true });
      }
      setSelectedInventoryItemKeys([]);
      setIsAddInventoryOpen(false);
      setSaveMessage({
        type: "success",
        text: res.data?.message || "Inventory item(s) added for selected work date.",
      });
    } catch (err) {
      setSaveMessage({
        type: "error",
        validationBlocks: [{
          messageVariant: "generic",
          title: "Cannot add inventory items",
          subtitle: err.response?.data?.message || err.message || "Failed to add inventory items.",
        }],
      });
    } finally {
      setAddingItems(false);
    }
  };

  const handleChangeField = (itemKey, field, value) => {
    if (
      (field === "takenToProject" || field === "returnedFromProject") &&
      !isValidPartialNumberInput(value)
    ) {
      return;
    }
    setTableRows((prev) =>
      prev.map((row) => {
        if (!row.isEditable) return row;
        return {
          ...row,
          items: row.items.map((item) => {
            if (item.itemKey !== itemKey) return item;
            const updated = { ...item, [field]: value };
            if (field === "takenToProject" || field === "returnedFromProject") {
              return applyTakenToProjectCalculations(updated);
            }
            return updated;
          }),
        };
      })
    );
  };

  const handleBlurField = (itemKey, field) => {
    if (field !== "takenToProject" && field !== "returnedFromProject") return;
    setTableRows((prev) =>
      prev.map((row) => {
        if (!row.isEditable) return row;
        return {
          ...row,
          items: row.items.map((item) => {
            if (item.itemKey !== itemKey) return item;
            const normalized = normalizeNumberOnBlur(item[field]);
            const normalizedStr = String(normalized);
            const current = item[field];
            if (current === normalizedStr || current === normalized) return item;
            const updated = { ...item, [field]: normalizedStr };
            if (field === "takenToProject" || field === "returnedFromProject") {
              return applyTakenToProjectCalculations(updated);
            }
            return updated;
          }),
        };
      })
    );
  };

  const handleChangeNotes = (itemKey, value) => {
    setTableRows((prev) =>
      prev.map((row) => {
        if (!row.isEditable) return row;
        return {
          ...row,
          items: row.items.map((item) =>
            item.itemKey === itemKey ? { ...item, dailyNotes: value } : item
          ),
        };
      })
    );
  };

  const dismissValidationBlock = (blockIndex) => {
    setSaveMessage((prev) => {
      if (!prev?.validationBlocks?.length) return null;
      const remaining = prev.validationBlocks.filter((_, i) => i !== blockIndex);
      if (remaining.length === 0) return null;
      return { ...prev, validationBlocks: remaining };
    });
  };

  const runSaveValidations = () => {
    const trimmedProject = (projectNo || "").trim();
    const trimmedDivision = (division || "").trim();
    const dateKey = (workDate || "").trim();

    if (!trimmedProject || !trimmedDivision || !dateKey) {
      setSaveMessage({
        type: "error",
        validationBlocks: [{
          messageVariant: "generic",
          title: "Cannot save — required fields missing",
          subtitle: "Project, division, and work date are required to save.",
        }],
      });
      return false;
    }

    if (!/^\d{4}-\d{2}-\d{2}$/.test(dateKey)) {
      setSaveMessage({
        type: "error",
        validationBlocks: [{
          messageVariant: "generic",
          title: "Cannot save — invalid work date",
          subtitle: "Work date must be YYYY-MM-DD.",
        }],
      });
      return false;
    }

    const editableRow = tableRows.find((r) => r.isEditable);
    if (!editableRow) {
      setSaveMessage({
        type: "error",
        validationBlocks: [{
          messageVariant: "generic",
          title: "Cannot save — no editable row",
          subtitle: "No editable inventory row for the selected work date.",
        }],
      });
      return false;
    }

    // No rows for the selected date — nothing to validate; allow Save and Finish.
    return true;
  };

  const handleSaveAndFinish = async () => {
    const baseUrl = process.env.REACT_APP_BACKEND_BASEURL;
    const trimmedProject = (projectNo || "").trim();
    const trimmedDivision = (division || "").trim();
    const dateKey = (workDate || "").trim();

    setSaveMessage(null);
    if (!runSaveValidations()) return;

    if (!baseUrl) {
      setSaveMessage({
        type: "error",
        validationBlocks: [{
          messageVariant: "generic",
          title: "Cannot save",
          subtitle: "Backend URL not configured.",
        }],
      });
      return;
    }

    const editableRow = tableRows.find((r) => r.isEditable);
    const entries = (editableRow?.items || []).map((item) => ({
      stockItemId: item.stockItemId ?? item.itemKey,
      takenToProject: item.takenToProject,
      returnedFromProject: item.returnedFromProject,
      totalEstimated: item.totalEstimated,
      dailyNotes: item.dailyNotes,
    }));

    setSaving(true);
    try {
      const { savedByName, savedByEmail } = getSavedByPayload(accounts);
      const res = await axios.post(
        `${baseUrl}/api/miter/production-performance/${encodeURIComponent(trimmedProject)}/inventory-entry/save`,
        {
          workDate: dateKey,
          division: trimmedDivision,
          entries,
          savedByName,
          savedByEmail,
        },
        await getAuthConfig()
      );
      if (res.data?.status === "success" && Array.isArray(res.data?.data?.tableRows)) {
        setTableRows(res.data.data.tableRows);
      } else {
        await fetchInventoryTable({ silent: true });
      }
      const successText = res.data?.message || "Inventory tracking saved.";
      setSaveMessage({ type: "success", text: successText });
      setSaving(false);
      closeAfterSaveTimeoutRef.current = window.setTimeout(() => {
        forceUnlockAllSheetModals();
        onClose?.();
      }, 1500);
      return;
    } catch (err) {
      setSaveMessage({
        type: "error",
        validationBlocks: [{
          messageVariant: "generic",
          title: "Cannot save",
          subtitle: err.response?.data?.message || err.message || "Failed to save inventory tracking.",
        }],
      });
    } finally {
      setSaving(false);
    }
  };

  const handleFlowStep = (step) => {
    onFlowNavigate?.(step, {
      projectNo,
      projectName,
      workDate,
      division,
    });
  };
  const flowNavDisabled = saving || tableLoading;
  const savedInventoryDateCount = useMemo(() => {
    const dates = new Set();
    tableRows.forEach((row) => {
      if (row.isEmptyForDate || !row.workDateIso) return;
      if (!Array.isArray(row.items) || row.items.length === 0) return;
      dates.add(row.workDateIso);
    });
    return dates.size;
  }, [tableRows]);

  return (
    <div className="flex min-h-0 h-full flex-1 flex-col overflow-hidden">
      <Header
        onClose={onClose}
        onFlowStep={onFlowNavigate ? handleFlowStep : undefined}
        navDisabled={flowNavDisabled}
      />
      <SaveValidationBanners
        validationBlocks={saveMessage?.validationBlocks}
        onDismiss={dismissValidationBlock}
      />
      <div className="nettles-flow-sheet-scroll flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden max-lg:landscape:overflow-y-auto max-lg:landscape:[scrollbar-gutter:stable] max-lg:landscape:[-webkit-overflow-scrolling:touch]">
      <div className="nettles-flow-sheet-body flex-1 min-h-0 min-w-0 overflow-y-auto overflow-x-clip max-lg:landscape:flex-none max-lg:landscape:overflow-y-visible">
        <DailyLogProjectFields
          projectNo={projectNo}
          projectName={projectName}
          workDate={workDate}
          onWorkDateChange={setWorkDate}
          activity={division}
        />

        {!tableLoading && (
          <div className="px-4 md:px-6 pt-3">
            <p className="text-xs text-gray-500">
              <span className="font-medium text-[#1E5A8A]">
                Total Inventory logged days: {savedInventoryDateCount}
              </span>
            </p>
          </div>
        )}

        <InventoryTable
          tableRows={tableRows}
          loading={tableLoading}
          onChangeField={handleChangeField}
          onBlurField={handleBlurField}
          onChangeNotes={handleChangeNotes}
          compactTopSpacing={!tableLoading}
        />
        <div className="mt-6 px-6 pb-6">
          <div className="flex justify-start">
            <div className="relative">
              <button
                type="button"
                onClick={() => setIsAddInventoryOpen(true)}
                disabled={tableLoading || !workDate || !division}
                className="h-10 px-6 rounded-md border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                Add Inventory Item
              </button>

              {isAddInventoryOpen && (
                <>
                  <div
                    className="fixed inset-0 z-[10170]"
                    onClick={() => setIsAddInventoryOpen(false)}
                    aria-hidden="true"
                  />
                  <div className="absolute left-0 bottom-12 z-[10171] w-[280px] rounded-lg bg-white border border-gray-200 shadow-xl">
                    <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
                      <h3 className="text-sm font-semibold text-gray-900">Add Inventory Item</h3>
                      <button
                        type="button"
                        onClick={() => setIsAddInventoryOpen(false)}
                        className="text-gray-400 hover:text-gray-600"
                        aria-label="Close"
                      >
                        ✕
                      </button>
                    </div>
                    <div className="px-4 py-3 max-h-[280px] overflow-y-auto">
                      {stockInventoryLoading ? (
                        <p className="text-sm text-gray-500">Loading inventory items…</p>
                      ) : stockInventoryError ? (
                        <p className="text-sm text-red-600">{stockInventoryError}</p>
                      ) : stockInventoryAddOptions.length === 0 ? (
                        <p className="text-sm text-gray-500">
                          No inventory items found for this division. Upload stock inventory items from Configuration Engine.
                        </p>
                      ) : availableInventoryAddOptions.length === 0 ? (
                        <p className="text-sm text-gray-500">
                          All inventory items are already in the table for this work date.
                        </p>
                      ) : (
                        <>
                          <p className="text-xs text-gray-500 mb-3">Select inventory items to add:</p>
                          <div className="space-y-2">
                            {availableInventoryAddOptions.map((opt) => (
                              <label
                                key={opt.id}
                                className="flex gap-2 items-center cursor-pointer"
                              >
                                <input
                                  type="checkbox"
                                  checked={selectedInventoryItemKeys.includes(opt.id)}
                                  onChange={(e) => {
                                    setSelectedInventoryItemKeys((prev) => {
                                      const set = new Set(prev);
                                      if (e.target.checked) set.add(opt.id);
                                      else set.delete(opt.id);
                                      return Array.from(set);
                                    });
                                  }}
                                  className="rounded border-gray-300"
                                />
                                <span className="text-sm text-gray-800">{opt.label}</span>
                              </label>
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                    <div className="px-4 py-3 border-t border-gray-200">
                      <button
                        type="button"
                        onClick={handleAddInventoryItemsUpdate}
                        disabled={selectedInventoryRows.length === 0 || addingItems}
                        className="w-full h-9 rounded-md bg-[#AD2226] text-white text-sm font-medium hover:bg-[#8F1B1E] disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {addingItems ? "Updating…" : "Update"}
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="shrink-0 border-t border-gray-200 bg-white px-4 sm:px-6 py-3 sm:py-4 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
        <div className="flex flex-col items-center gap-2">
          {saveMessage?.type === "success" && saveMessage.text && (
            <p className="text-center text-sm px-1 text-green-600 max-w-[42rem]">
              {saveMessage.text}
            </p>
          )}
          <div className="flex w-full flex-nowrap sm:flex-wrap items-center justify-center gap-3">
            {onFlowNavigate && (
              <DailyLogBackButton
                currentStep="inventory"
                onStepChange={handleFlowStep}
                disabled={flowNavDisabled}
              />
            )}
            <button
              type="button"
              onClick={handleSaveAndFinish}
              disabled={saving || tableLoading || saveMessage?.type === "success"}
              className={`h-12 sm:h-11 min-w-0 flex-1 sm:flex-none sm:w-auto sm:max-w-none sm:px-16 rounded-md text-white text-sm font-semibold touch-manipulation ${
                saving || tableLoading || saveMessage?.type === "success"
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-[#AD2226] hover:bg-[#8F1B1E]"
              }`}
            >
              {saving ? "Saving…" : "Save and Finish"}
            </button>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};

const InventoryTrackingModal = ({
  isOpen,
  onClose,
  initialContext,
  onFlowNavigate,
  preserveWhenHidden = false,
}) => {
  useFullscreenSheetModal(isOpen);
  useEffect(() => {
    if (!isOpen || preserveWhenHidden) return undefined;
    return () => {
      forceUnlockAllSheetModals();
    };
  }, [isOpen, preserveWhenHidden]);

  if (!isOpen && !preserveWhenHidden) return null;

  return createPortal(
    <div
      aria-hidden={!isOpen}
      className={`fixed inset-0 z-[10170] items-stretch justify-center bg-black/50 ${
        isOpen ? 'flex' : 'hidden'
      }`}
    >
      <div className="nettles-flow-sheet-panel absolute inset-2 lg:inset-6 bg-white rounded-xl shadow-2xl flex flex-col overflow-x-clip overflow-y-hidden max-lg:top-[max(0.5rem,env(safe-area-inset-top))] max-lg:bottom-[max(0.5rem,env(safe-area-inset-bottom))] max-lg:left-[max(0.5rem,env(safe-area-inset-left))] max-lg:right-[max(0.5rem,env(safe-area-inset-right))]">
        <InventoryTrackingContent
          onClose={onClose}
          initialContext={initialContext}
          onFlowNavigate={onFlowNavigate}
          isActive={isOpen}
        />
      </div>
    </div>,
    document.body
  );
};

export default InventoryTrackingModal;
